import CancelIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import jsonExport from 'jsonexport/dist';
import cloneDeep from 'lodash/cloneDeep';
import defaultsDeep from 'lodash/defaultsDeep';
import isUndefined from "lodash/isUndefined";
import { useEffect, useState } from "react";
import {
  Button,
  DatagridConfigurable,
  downloadCSV,
  FunctionField,
  List,
  Loading,
  SelectField,
  ShowButton,
  TextField,
  useDataProvider,
  useNotify,
  useRedirect,
  useRefresh,
} from 'react-admin';
import { parseIdRecordsforExport } from "../../mappers/IdentityVerification";
import CustomDialog from "../../utils/CustomDialog";
import CustomTooltip from "../../utils/CustomTooltip";
import { isRequestCompleted } from "../../utils/WatchListUtils";
import { CustomDataGridHeader, onToggleDataGridHeader } from '../common/CustomDataGridHeader';
import { CustomPagination } from "../common/CustomPaginationComponent";
import ListActions from "../common/ListActions";
import { ActionTitles, IDENTITY_VERIFICATION_REQUEST_STATUS_CHOICES, RESOURCES, RESOURCES_BASEPATH, RESOURCES_META_DATA, SCREENING_MATCH_STATUS_ICON_FOR_LIST, SCREENING_MATCH_STATUS_KEYS, SCREENING_MATCH_STATUS_TOOLTIP_TITLE } from "../Constants";
import { isAdminUser } from "../DataProvider";
import { Error404 } from "../errors/Error404";
import {
  cellContentCentered,
  dataGrid,
  iconAction,
  listRoot,
  textColor,
} from '../Styles';
import { IdentityAdminFilter, IdentityFilter } from "./IdentityFilter";
import { DaysToRenewalField } from "./DaysToRenewalField";
import { ScreeningReportGrid } from "./ScreeningReportDialogGrid";

export const IdReports = (props) => {
  const isAdmin = isAdminUser();
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(undefined);
  const [pauseCMLoading, setPauseCMLoading] = useState(false);

  const dataProvider = useDataProvider();
  const [data, setData] = useState();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState();
  const [clientId, setClientId] = useState(undefined);
  let dataGridsLS = JSON.parse(localStorage.getItem("dataGrids"))
  if (!dataGridsLS)
    dataGridsLS = { "ids": true }
  const [dataGrids, setDataGrids] = useState(dataGridsLS ? dataGridsLS : {});

  useEffect(() => {
    if(!isAdmin){
        dataProvider
        .getMappedUsersSummary(`client/mapped-users`)
        .then(({ data }) => {
          setData(data);
          setLoading(false);
        })
        .catch((error) => {
          setError(error);
          setLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataProvider]);

  useEffect(()=>{
    if(isAdmin && !isUndefined(clientId)){
      dataProvider
      .getMappedUsersbyClientId({clientId : clientId})
      .then(({ data }) => {
        setData(data);
        setLoading(false);
      })
      .catch((error) => {
        setError(error);
        setLoading(false);
      });
  }
  }, [clientId, dataProvider, isAdmin])
  
  const pauseContinuousMonitoring = () => {
    setPauseCMLoading(true);
    dataProvider
    .pauseContinuousMonitoringForAllReport({ id: selectedRecord.id })
    .then(() => {
      handleDialogClose();
      notify('Continuous monitoring disabled for all reports', "info", { autoHideDuration: 1000 });      
      refresh();
    })
    .catch((err) => {
      notify(err?.body ? err.body : 'Something went wrong while disabling continuous monitoring', "warning", { autoHideDuration: 1000 });
    }).finally(() => {
      setPauseCMLoading(false);
    });
  }

  const handleDialogClose = () => {
    setDialogOpen(false);    
  };

  const handleReportDialogClose = () => {
    setReportDialogOpen(false);
  };
  
  const handleReportDialogOpen = (record) => {
    setSelectedRecord(record);
    setReportDialogOpen(true);
  }
  const handleIconClick = (record) => {    
    setSelectedRecord(record)
    setDialogOpen(true);
  };

  const exporter = (records) => {    
    const list = parseIdRecordsforExport(records, data);
    
    return jsonExport(list, (err, csv) => {
        downloadCSV(csv, RESOURCES_META_DATA[RESOURCES.IDENTITY_VERIFICATION].displayName);        
    });  
  };

  if (loading && !isAdmin) return <Loading />;
  if (error) return <Error404 />;

  return (
    <List
      sx={listRoot}
      actions={
        <ListActions
          title={ActionTitles.IDENTITY_VERIFICATIONS}
          basePath={RESOURCES_BASEPATH.IDENTITY_VERIFICATION}
          renderButtonAndSearch={true}
          showCreateButton={false}
          ShowExportButton={true}
          showColumnsButton={true}
          filters={isAdmin
            ? <IdentityAdminFilter requestingUserNames={data} setClientId={setClientId} /> 
            : <IdentityFilter requestingUserNames={data} /> }
        />
      }
      sort={{ field: 'updatedAt', order: 'DESC' }}      
      pagination={<CustomPagination />}
      empty={false}
      exporter={exporter}
      filterDefaultValues={{ screeningMatchStatus : SCREENING_MATCH_STATUS_KEYS.MATCH_PENDING }}
    >
      <DatagridConfigurable
        //omit prop won't work as we're using custom logic to hide fields.
        header={
          <CustomDataGridHeader
            fixedColumn={dataGrids['ids']}
            onToggle={() => onToggleDataGridHeader('ids', dataGrids, setDataGrids)}
          />
        }
        sx={
          defaultsDeep(
            cloneDeep(dataGrid.base),
            dataGrids['ids'] ? cloneDeep(dataGrid.firstColSticky) : {}
          )
        }
        bulkActionButtons={false}
      >        
        <TextField source="name" label="Contact Name" sortable={false} />
        <TextField sx={textColor.primary} source="emailAddress" label="Email Address" sortable={false} />
        {isAdmin && <SelectField choices={IDENTITY_VERIFICATION_REQUEST_STATUS_CHOICES} source="identityRequestStatus" label="Identity Status" sortable={false}/>}
        <FunctionField
          sx={cellContentCentered}
          label="Match Status"
          source="matchStatus"
          sortable={false}
          render={(record) => {
            return ( record.screeningMatchStatus !== SCREENING_MATCH_STATUS_KEYS.NO_MATCH )
            ?               
              (
                <CustomTooltip
                  title={SCREENING_MATCH_STATUS_TOOLTIP_TITLE[record["screeningMatchStatus"]]}
                  placement="top"
                >
                  <Button
                    id={`identity-list-match-status-button-${record.id}`}
                    onClick={() => handleReportDialogOpen(record)}
                    sx={iconAction}
                    key="button"
                  >
                    {SCREENING_MATCH_STATUS_ICON_FOR_LIST[record["screeningMatchStatus"]]}
                  </Button>
                </CustomTooltip>
              )
            : (<></>)
          }}
        />
        <FunctionField
          sx={cellContentCentered}
          label="Continuous Monitoring"
          source="continuousMonitoring"
          sortable={false}
          render={(record) => (
            <> 
              {record.continuousMonitoringEnabled 
                ?
                 isAdmin 
                 ?
                  <CheckCircleIcon sx={{...textColor.green}} />
                 : 
                  <CustomTooltip title="Click to disable continuous monitoring" placement="top">
                    <Button
                      id={`identity-list-disable-monitoring-${record.id}`}
                      onClick={!isAdmin && (() => handleIconClick(record))}
                      sx={iconAction}
                      key="button"
                    >
                      <CheckCircleIcon
                        sx={{...textColor.green, fontSize: '1.5rem !important'}}
                      />
                    </Button>
                  </CustomTooltip>                                   
                : 
                <CancelIcon sx={textColor.red} />
              }
            </> 
          )}
        />
        <TextField source="cmStartedAt" label="Monitoring Started" sortable={false} />
        <TextField source="cmFinishedAt" label="Monitoring End" sortable={false} />
        <FunctionField
          sx={cellContentCentered}
          label="Days to Renewal"
          source="daysToRenewal"
          render={(record) =>             
            record?.daysToRenewal
            ? <DaysToRenewalField days={record.daysToRenewal}/>
            : <></>
          }
        />
        <TextField source="createdAt" label="Created" sortable={true} />
        <TextField source="updatedAt" label="Last Modified" sortable={true} />
        <FunctionField
          render={(record) =>
            !isUndefined(data) && data.get(record.requestingUserId) != null ? data.get(record.requestingUserId) : ' '
          }
          label="Requesting User Name"
          source="requestingUserName"
          sortable={false}
        />
        <FunctionField
          sx={cellContentCentered}
          source="viewRequest"
          label={false}
          sortable={false}
          render={(record) => (
            <CustomTooltip title="View Request" placement="top">
              <Button
                id={`view-request-${record.id}`}
                sx={iconAction}
                size="medium"
                label={""}
                onClick={() => {
                  redirect(
                    isAdmin 
                    ? `${RESOURCES_BASEPATH.REPORTS}/${encodeURIComponent('client/' + clientId + '/detail/' + record['clientRequestId'])}/show` 
                    : `${RESOURCES_BASEPATH.REPORTS}/${record['clientRequestId']}/show`);
                }}
              >
                <DescriptionOutlinedIcon sx={textColor.black} />
              </Button>
            </CustomTooltip>
          )}
        />
        <FunctionField
          sx={cellContentCentered}
          source="view"
          label={false}
          sortable={false}
          render={(record) => (
            <CustomTooltip title="View" placement="top">
              <ShowButton
                id={`show-watchlist-${record.id}`}
                icon={<RemoveRedEyeOutlinedIcon />}
                sx={iconAction}
                size="medium"
                label={""}
                basePath={RESOURCES_BASEPATH.IDENTITY_VERIFICATION}
                record={record}
              ></ShowButton>
            </CustomTooltip>
          )}
        />
      </DatagridConfigurable>
        <> 
        {!isAdmin && 
        <CustomDialog
          openDialog={dialogOpen}
          closeDialog={handleDialogClose}
          title="Continuous Monitoring Details"
          content={
            isRequestCompleted(selectedRecord?.identityRequestStatus)
            ? <p>Are you sure you want to disable continuous monitoring for all reports?</p>
            : <p>As the request is still in the new state, continuous monitoring cannot be disabled for all reports at the moment</p>
          }
          submitBtnTitle="DISABLE MONITORING"                
          handleSubmit={pauseContinuousMonitoring}
          hideDivider={true}
          disableSubmitButton={!isRequestCompleted(selectedRecord?.identityRequestStatus) || pauseCMLoading}
          loading={pauseCMLoading}
        />}
        <CustomDialog
          openDialog={reportDialogOpen}
          closeDialog={handleReportDialogClose}
          title="Screening Report Details"
          content={<ScreeningReportGrid record={selectedRecord} />}
          hideSubmitBtn
          hideDivider={true}
        />
        </>
    </List>
  );
};